<template>
  <v-container fluid class="ma-3">
    <page-loader v-if="isLoading" />

    <div v-else-if="healthCheck">
      <v-row class="mb-5">
        <v-col cols="auto">
          <v-card width="550" color="grey lighten-4">
            <v-card-title>
              {{ healthCheck.templateName }} - Report
            </v-card-title>
            <v-divider></v-divider>

            <v-card-text>
              <v-simple-table class="summary-table">
                <template v-slot:default>
                  <tbody>
                    <tr v-if="healthCheck.companyName">
                      <td>Company Name</td>
                      <td>{{ healthCheck.companyName }}</td>
                      <td style="width: 1px"></td>
                    </tr>
                    <tr>
                      <td>Overall Score</td>
                      <td>{{ overallScore }}%</td>
                      <td style="width: 1px">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-progress-circular
                              v-bind="attrs"
                              v-on="on"
                              class="mr-3"
                              :rotate="360"
                              :size="30"
                              :width="5"
                              :value="overallScore"
                              :color="
                                overallScore === 100 ? 'success' : 'error'
                              "
                            >
                            </v-progress-circular>
                          </template>
                          <span>{{ overallScore }}%</span>
                        </v-tooltip>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Compliance Score
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              class="ml-2"
                              size="medium"
                              color="info"
                              >info_outline</v-icon
                            >
                          </template>
                          <span
                            >Compliance items are those necessary to ensure a
                            safe workplace</span
                          >
                        </v-tooltip>
                      </td>
                      <td>
                        <div class="my-2">{{ complianceScore }}%</div>
                      </td>
                      <td style="width: 1px">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-progress-circular
                              v-bind="attrs"
                              v-on="on"
                              class="mr-3"
                              :rotate="360"
                              :size="30"
                              :width="5"
                              :value="complianceScore"
                              :color="
                                complianceScore === 100 ? 'success' : 'error'
                              "
                            >
                            </v-progress-circular>
                          </template>
                          <span>{{ complianceScore }}%</span>
                        </v-tooltip>
                      </td>
                    </tr>
                    <tr>
                      <td>Link</td>
                      <td colspan="2">
                        <v-text-field
                          id="healthCheck_link"
                          readonly
                          :value="healthCheckLink"
                        >
                          <template v-slot:append-outer>
                            <v-tooltip :close-delay="1800" bottom>
                              <template v-slot:activator="{ on }">
                                <v-icon
                                  v-on="on"
                                  @click="copyLinkToClipboard"
                                  @mouseleave="
                                    copyToClipboardText = 'Copy to clipboard'
                                  "
                                >
                                  content_copy
                                </v-icon>
                              </template>
                              {{ copyToClipboardText }}
                            </v-tooltip>
                          </template>
                        </v-text-field>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <span class="mr-2 grey--text">
                Created on {{ healthCheck.createdDateUtc | formatDateTime }}
              </span>
            </v-card-actions>
          </v-card>
        </v-col>

        <v-col
          v-if="healthCheck.comments || totalIncompleteComplianceCount > 0"
        >
          <v-alert
            v-if="totalIncompleteComplianceCount > 0"
            type="error"
            icon="privacy_tip"
            border="left"
            max-width="550"
          >
            <span v-if="totalIncompleteComplianceCount === 1">
              You have <strong>1</strong> unactioned item that is necessary to
              ensure a safe workplace. It is recommended that you take action on
              this as soon as possible.
            </span>
            <span v-else>
              You have
              <strong>{{ totalIncompleteComplianceCount }}</strong> unactioned
              items that are necessary to ensure a safe workplace. It is
              recommended that you take action on these as soon as possible.
            </span>
          </v-alert>

          <v-card v-if="healthCheck.comments" class="mr-4">
            <v-card-subtitle
              ><v-icon class="mr-2" size="medium">article</v-icon
              >Notes</v-card-subtitle
            >
            <v-card-text>
              {{ healthCheck.comments }}
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <template v-for="(group, index) in healthCheck.groups">
        <v-card :key="index" class="mb-8 mt-3">
          <v-card-title>
            {{ group.name }}

            <v-chip v-if="anyScorableItemsWithinGroup(group)"
              :color="getGroupHeaderColour(group)"
              dark
              class="ml-2 px-2"
              style="height: 22px"
              >{{ getGroupCompletionPc(group) }}%</v-chip
            >
          </v-card-title>

          <v-card-text>
            <v-alert
              :value="getGroupComplianceIncompleteCount(group) > 0"
              outlined
              type="error"
              icon="privacy_tip"
            >
              <span v-if="getGroupComplianceIncompleteCount(group) === 1">
                There is <strong>1</strong> unactioned compliance item in this
                section that is necessary to ensure a safe workplace.
              </span>
              <span v-else>
                There are
                <strong>{{ getGroupComplianceIncompleteCount(group) }}</strong>
                unactioned compliance items in this section that are necessary
                to ensure a safe workplace.
              </span>
            </v-alert>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Answer</th>
                    <th class="text-left">Question</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="item in group.items">
                    <tr
                      v-if="$_shouldDisplayQuestion(item, healthCheck)"
                      :key="item.id"
                      :class="{
                        'compliance-incomplete':
                          item.result == false &&
                          $_isComplianceQuestion(item, healthCheck),
                      }"
                    >
                      <td>
                        <v-tooltip bottom v-if="item.result == true">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-if="item.result == true"
                              v-bind="attrs"
                              v-on="on"
                              :color="getResultButtonColour(item)"
                              >check_circle</v-icon
                            >
                          </template>
                          <span>Answer: Yes</span>
                        </v-tooltip>

                        <v-tooltip
                          bottom
                          v-else-if="$_isComplianceQuestion(item, healthCheck)"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon color="red" v-bind="attrs" v-on="on"
                              >cancel</v-icon
                            >
                          </template>
                          <span
                            >Answer: No
                            <div class="font-italic">
                              This is necessary to ensure a safe workplace.
                            </div></span
                          >
                        </v-tooltip>

                        <v-tooltip bottom v-else>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon color="red" v-bind="attrs" v-on="on"
                              >cancel</v-icon
                            >
                          </template>
                          <span>Answer: No</span>
                        </v-tooltip>
                      </td>
                      <td>
                        <div
                          v-if="$_isComplianceQuestion(item, healthCheck)"
                          class="mt-2"
                        >
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <span
                                v-bind="attrs"
                                class="caption warning--text"
                                v-on="on"
                              >
                                <v-icon
                                  class="mr-1"
                                  size="medium"
                                  color="warning"
                                  >privacy_tip</v-icon
                                >Compliance
                              </span>
                            </template>
                            <span
                              >This is necessary to ensure a safe
                              workplace.</span
                            >
                          </v-tooltip>
                        </div>

                        <div class="my-2">{{ item.question }}</div>
                        <v-alert
                          :value="
                            item.comments != null && item.comments.length > 0
                          "
                          class="mt-3 caption"
                          color="cyan"
                          border="left"
                          elevation="2"
                          colored-border
                          icon="chat"
                          dense
                        >
                          {{ item.comments }}
                        </v-alert>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </template>
    </div>

    <v-snackbar
      v-model="snackbar.show"
      top
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          :color="snackbar.buttonColor"
          v-bind="attrs"
          text
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import PageLoader from "@/components/PageLoader";
import healthCheckApi from "@/api/healthCheck.api";

export default {
  components: {
    PageLoader,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      healthCheck: null,
      isLoading: false,
      snackbar: {
        show: false,
        message: "",
        color: undefined,
        buttonColor: undefined,
        timeout: 6000,
      },
      copyToClipboardText: "Copy to clipboard",
    };
  },
  async created() {
    try {
      this.isLoading = true;
      this.healthCheck = await healthCheckApi.getById(this.id);
    } catch (ex) {
      if (ex.response && ex.response.status === 404) {
        this.showSnackbar("Health Check could not be found", true);
      } else {
        this.showSnackbar("Failed to load", true);
      }
    } finally {
      this.isLoading = false;
    }
  },
  computed: {
    totalScorableQuestionCount() {
      if (!this.healthCheck) return 0;
      return this.healthCheck.groups.reduce((allTotal, currentGroup) => {
        return currentGroup.items.reduce((groupTotal, currentItem) => {
          return this.$_isScorableQuestion(currentItem, this.healthCheck)
            ? groupTotal + 1
            : groupTotal;
        }, allTotal);
      }, 0);
    },
    totalComplianceQuestionCount() {
      if (!this.healthCheck) return 0;
      return this.healthCheck.groups.reduce((allTotal, currentGroup) => {
        return currentGroup.items.reduce((groupTotal, currentItem) => {
          return (
            groupTotal +
            (this.$_shouldDisplayQuestion(currentItem, this.healthCheck) &&
            this.$_isComplianceQuestion(currentItem, this.healthCheck)
              ? 1
              : 0)
          );
        }, allTotal);
      }, 0);
    },
    totalIncompleteComplianceCount() {
      if (!this.healthCheck) return 0;
      return this.healthCheck.groups.reduce((allTotal, currentGroup) => {
        return currentGroup.items.reduce((groupTotal, currentItem) => {
          if (
            this.$_shouldDisplayQuestion(currentItem, this.healthCheck) &&
            currentItem.result == false &&
            this.$_isComplianceQuestion(currentItem, this.healthCheck)
          ) {
            return groupTotal + 1;
          } else return groupTotal;
        }, allTotal);
      }, 0);
    },
    overallScore() {
      if (!this.healthCheck) return 0;

      const complete = this.healthCheck.groups.reduce(
        (allTotal, currentGroup) => {
          return currentGroup.items.reduce((groupTotal, currentItem) => {
            const answered =
              this.$_shouldDisplayQuestion(currentItem, this.healthCheck) &&
              currentItem.questionType !== 2 &&
              currentItem.result == true;
            return groupTotal + (answered ? 1 : 0);
          }, allTotal);
        },
        0
      );

      if (complete === 0 || this.totalScorableQuestionCount === 0) return 0;

      return Math.round((complete / this.totalScorableQuestionCount) * 100);
    },
    complianceScore() {
      if (!this.healthCheck) return 0;

      const complete = this.healthCheck.groups.reduce(
        (allTotal, currentGroup) => {
          return currentGroup.items.reduce((groupTotal, currentItem) => {
            const answered =
              this.$_shouldDisplayQuestion(currentItem, this.healthCheck) &&
              this.$_isComplianceQuestion(currentItem, this.healthCheck) &&
              currentItem.result == true;
            return groupTotal + (answered ? 1 : 0);
          }, allTotal);
        },
        0
      );

      if (complete === 0 || this.totalComplianceQuestionCount === 0) return 0;

      return Math.round((complete / this.totalComplianceQuestionCount) * 100);
    },
    healthCheckLink() {
      return `https://healthcheck.yourhrteam.com.au/${this.id}`;
    },
  },
  methods: {
    showSnackbar(message, isError) {
      this.snackbar.show = true;
      this.snackbar.message = message;
      this.snackbar.color = isError ? "red accent-2" : undefined;
      this.snackbar.timeout = isError ? 0 : 6000;
    },
    getGroupHeaderColour(group) {
      const completionPc = this.getGroupCompletionPc(group);
      if (completionPc === 100) return "success";
      else return "error";
    },
    anyScorableItemsWithinGroup(group) {
      return group.items.filter(
        (item) => this.$_isScorableQuestion(item, this.healthCheck)
      ).length > 0
    },
    getGroupCompletionPc(group) {
      const complete = group.items.filter(
        (item) =>
          this.$_shouldDisplayQuestion(item, this.healthCheck) &&
          item.questionType !== 2 &&
          item.result == true
      ).length;
      const total = group.items.filter(
        (item) =>
          this.$_shouldDisplayQuestion(item, this.healthCheck) &&
          item.questionType !== 2
      ).length;

      if (complete === 0 || total === 0) return 0;

      return Math.round((complete / total) * 100);
    },
    getGroupComplianceIncompleteCount(group) {
      return group.items.filter(
        (item) =>
          this.$_shouldDisplayQuestion(item, this.healthCheck) &&
          item.result == false &&
          this.$_isComplianceQuestion(item, this.healthCheck)
      ).length;
    },
    getResultButtonColour({ questionType, result }) {
      if (questionType === 2) return "default";
      return result ? "success" : "error";
    },
    copyLinkToClipboard() {
      // Get the text field
      var copyText = document.getElementById("healthCheck_link");

      // Select the text field
      copyText.select();
      copyText.setSelectionRange(0, 99999); // For mobile devices

      // Copy the text inside the text field
      navigator.clipboard.writeText(copyText.value);

      this.copyToClipboardText = "Copied to clipboard!";
    },
  },
};
</script>

<style scoped>
tr.compliance-incomplete {
  background-color: #ffe0b2;
}
tr.compliance-incomplete:hover {
  background-color: #ffe0b2 !important;
}
.summary-table tr > td:first-child {
  width: 185px;
}
</style> 